// Web-exclusive Feedback screen
// Simple feedback submission form

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import WebLayout from '../../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../../constants/webStyles';
import { submitFeedback } from '../../../services/shared';
import { useIsMobile } from '../../../hooks/useIsMobile';

const FeedbackScreen = () => {
  const navigation = useNavigation();
  const isMobile = useIsMobile();

  // Form state
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [errorFields, setErrorFields] = useState([]);

  // Toast state
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Toast notification helper
  const showToast = (message, type = 'info') => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 3000);
  };

  // Submit feedback mutation
  const submitMutation = useMutation({
    mutationFn: submitFeedback,
    onSuccess: () => {
      setFeedbackMessage('');
      setErrorFields([]);
      showToast('Feedback submitted successfully! Thank you for helping us improve.', 'success');
    },
    onError: (err) => {
      showToast(
        err?.response?.data?.message || 'Failed to submit feedback',
        'error'
      );
    },
  });

  // Form validation
  const validateForm = () => {
    const errors = [];
    if (!feedbackMessage?.trim()) errors.push('feedbackMessage');
    setErrorFields(errors);
    return errors.length === 0;
  };

  // Handle submit
  const handleSubmit = () => {
    if (!validateForm()) return;

    submitMutation.mutate({
      type: 'feedback',
      content: feedbackMessage.trim(),
    });
  };

  // Handle back navigation
  const handleBack = () => {
    navigation.goBack();
  };

  const isSaving = submitMutation.isPending;

  return (
    <WebLayout>
      {/* Toast Notification */}
      {toast && (
        <View
          style={[
            styles.toast,
            toast.type === 'success' && styles.toastSuccess,
            toast.type === 'error' && styles.toastError,
          ]}
        >
          <Text style={styles.toastText}>{toast.message}</Text>
        </View>
      )}

      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <TouchableOpacity style={styles.backButton} onPress={handleBack}>
            <View style={styles.backButtonContent}>
              <ArrowLeft size={16} color={webColors.textSecondary} />
              <Text style={styles.backButtonText}>Back to Settings</Text>
            </View>
          </TouchableOpacity>
          <View style={styles.titleRow}>
            <MessageSquare size={28} color={webColors.primary} />
            <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Feedback</Text>
          </View>
          <Text style={styles.pageSubtitle}>
            Help us improve Supermedic by sharing your thoughts and suggestions.
          </Text>
        </View>

        {/* Content Card */}
        <View style={styles.contentCard}>
          <Text style={styles.sectionTitle}>Your Feedback</Text>
          <Text style={styles.formDescription}>
            We value your input! Let us know what you think about Supermedic,
            what features you'd like to see, or any issues you've encountered.
          </Text>

          <TextInput
            style={[
              styles.formInput,
              errorFields.includes('feedbackMessage') && styles.formInputError,
            ]}
            value={feedbackMessage}
            onChangeText={(v) => {
              setFeedbackMessage(v);
              if (errorFields.includes('feedbackMessage')) {
                setErrorFields((prev) => prev.filter((f) => f !== 'feedbackMessage'));
              }
            }}
            placeholder="Share your feedback, suggestions, or concerns..."
            placeholderTextColor={webColors.textMuted}
            multiline
            textAlignVertical="top"
          />

          {/* Action Buttons */}
          <View style={styles.actionRow}>
            <TouchableOpacity style={styles.backBtn} onPress={handleBack}>
              <Text style={styles.backBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.submitButton, isSaving && styles.buttonDisabled]}
              onPress={handleSubmit}
              disabled={isSaving}
            >
              <Text style={styles.submitButtonText}>
                {isSaving ? 'Submitting...' : 'Submit Feedback'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={{ height: 60 }} />
      </ScrollView>
    </WebLayout>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },

  // Toast
  toast: {
    position: 'absolute',
    top: 80,
    right: 20,
    backgroundColor: webColors.cardBackground,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: webBorderRadius.small,
    ...webShadows.modal,
    zIndex: 1000,
    minWidth: 300,
    borderLeftWidth: 4,
    borderLeftColor: webColors.info,
  },
  toastSuccess: {
    borderLeftColor: webColors.success,
  },
  toastError: {
    borderLeftColor: webColors.error,
  },
  toastText: {
    fontSize: 14,
    color: webColors.textPrimary,
  },

  // Header
  pageHeader: {
    marginBottom: 24,
  },
  backButton: {
    marginBottom: 16,
  },
  backButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  pageTitleMobile: {
    fontSize: 22,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textSecondary,
  },

  // Content Card
  contentCard: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    padding: webSpacing.cardPadding,
    ...webShadows.card,
  },

  // Section Title
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 12,
  },
  formDescription: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 20,
    lineHeight: 22,
  },

  // Form
  formInput: {
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 15,
    color: webColors.textPrimary,
    backgroundColor: webColors.cardBackground,
    minHeight: 200,
  },
  formInputError: {
    borderColor: webColors.error,
    borderWidth: 2,
  },

  // Action Row
  actionRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  backBtn: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
  },
  backBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  submitButton: {
    backgroundColor: webColors.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  submitButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});

export default FeedbackScreen;
